/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA
 */

import router from '@/core/router/router';
import CookiePolicyWrapper from '@/component/CookiePolicyWrapper/CookiePolicyWrapper.svelte';

let container;

/**
 * Extends the provided controller object with page controller functions. This is required so
 * the controller can be handled by router (from tao-core-app/src/core/router). Provided
 * controller requires implementation of start method so it can be init by router.
 *
 * Usage:
 *  import pageController from 'controller/page';
 *  const controller = pageController(controller, {
 *      foo() {
 *          // in controller functions, page controller functions are available
 *          this.router();
 *      }
 *  });
 *
 * @param {Object} controller - controller object that should be extended
 * @returns {Object} ontroller object that is extended with page controller functions.
 */
export default controller =>
    // eslint-disable-next-line implicit-arrow-linebreak
    Object.assign(controller, {
        /**
         * Flag defining if component has been destroyed
         */
        destroyed: false,

        /**
         * Getter of page container
         * @returns {Element} Page container element
         */
        get container() {
            return container || (container = document.querySelector('#page'));
        },

        /**
         * Getter of router
         * @returns {Router} Router instance
         */
        get router() {
            return router;
        },

        /**
         * Common prepare function before controller starts
         */
        prepare() {},

        /**
         * Mounts additional components such as CookiePolicyWrapper into the controller's container.
         * Typically called after the main Svelte component is initialized.
         *
         * @returns {void}
         */
        mount() {
            this.cookiePolicyWrapper = new CookiePolicyWrapper({
                target: this.container
            });
        },
        /**
         * Common clean function after controller stopped
         */
        clean() {
            const pageContainer = this.container;
            pageContainer.classList.remove(this.name);
        },

        /**
         * Function to be called directly in router to avoid any rewriting for default hooks
         * Can be asynchronous
         */
        async destroyAdditionalComponents() {
            if (this.cookiePolicyWrapper) {
                this.cookiePolicyWrapper.$destroy();
            }
        },
        /**
         * Destroy the component
         */
        destroy() {
            this.destroyed = true;
            if (this.component) {
                this.component.$destroy();
            }
        }
    });
